/**
 * Agent-side Web Push.
 *
 * Unlike the widget, the dashboard is same-origin with its own service worker,
 * so it can register one directly. The server pushes to agents whenever a
 * visitor message lands and no agent socket is watching the app stream.
 */
import { api } from './api.js';

function urlBase64ToUint8Array(base64) {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const normalized = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(normalized);
  return Uint8Array.from(raw, (char) => char.charCodeAt(0));
}

export const pushSupported = () =>
  'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

export async function currentSubscription() {
  if (!pushSupported()) return null;
  const registration = await navigator.serviceWorker.getRegistration();
  return registration?.pushManager.getSubscription() ?? null;
}

export async function enablePush(appId) {
  if (!pushSupported()) throw new Error('This browser cannot deliver push notifications');

  const { publicKey, enabled } = await api.push.publicKey(appId);
  if (!enabled || !publicKey) {
    // VAPID keys are unset server-side; nothing to subscribe to.
    throw new Error('Push is not configured on this server');
  }

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') throw new Error('Notification permission denied');

  const registration = await navigator.serviceWorker.register('/sw.js');
  await navigator.serviceWorker.ready;

  const existing = await registration.pushManager.getSubscription();
  const subscription =
    existing ??
    (await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    }));

  const json = subscription.toJSON();
  await api.push.subscribe(appId, { endpoint: json.endpoint, keys: json.keys });
  return subscription;
}

export async function disablePush(appId) {
  const subscription = await currentSubscription();
  if (!subscription) return;
  await api.push.unsubscribe(appId, subscription.endpoint).catch(() => {});
  await subscription.unsubscribe().catch(() => {});
}

export default { enablePush, disablePush, pushSupported, currentSubscription };
